import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  fetchBeatCustomerCategoryCompReport,
  fetchBeatCustomerOutstandingReport,
  fetchBeatCustomerSalesCompReport,
  fetchTargetSaleReport
} from 'Service/ApiService';

const BeatsCustomerReportController = () => {
  const location = useLocation();
  const customer = location.state?.customer;
  const user = JSON.parse(localStorage.getItem('user'));

  const formatIndianShort = (num) => {
    const n = Number(num || 0);
    const sign = n < 0 ? '-' : '';
    const abs = Math.abs(n);

    if (abs >= 10000000) return `${sign}${(abs / 10000000).toFixed(2)} Cr`;
    if (abs >= 100000) return `${sign}${(abs / 100000).toFixed(2)} L`;
    if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(2)} K`;
    return `${sign}${abs}`;
  };

  // Financial year info
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1;
  const startYear = month >= 4 ? year : year - 1; // FY starts in April
  const currentFY = `${String(startYear).slice(2)}-${String(startYear + 1).slice(2)}`;
  const previousFY = `${String(startYear - 1).slice(2)}-${String(startYear).slice(2)}`;

  // States
  const [cloading, setCloading] = useState(false);
  const [outstanding, setOutstanding] = useState([]);
  const [sales, setSales] = useState([]);
  const [categoryComp, setCategoryComp] = useState([]);
const [targetsales, setTargetSales] = useState([]);
  // Fetch all reports on mount
  useEffect(() => {
    fetchAllReports();
  }, []);

  const fetchAllReports = async () => {
    setCloading(true);
    try {
      await Promise.all([
        getOutstandingReport(),
        getSalesCompReport(),
        getCategoryCompReport(),
        getTargetSale()
      ]);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setCloading(false);
    }
  };

  // Outstanding Report
  const getOutstandingReport = async () => {
    try {
      const response = await fetchBeatCustomerOutstandingReport(user.userCode, customer.party_code);
      if (response.success) {
        setOutstanding(
          response.data.partyOutstanding.map((row) => ({
            ...row,
            billAmount: row.billAmount,
            billAmountDisplay: formatIndianShort(row.billAmount)
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching Outstanding Report:', error);
    }
  };

  // Sales Comparison Report
  const getSalesCompReport = async () => {
    try {
      const response = await fetchBeatCustomerSalesCompReport(user.userCode, customer.party_code);
      if (response.success) {
        const monthOrder = [
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
          'January',
          'February',
          'March'
        ];

        // Backward compatibility: support both old keys (ctd/ytd) and new keys (current_year/previous_year)
        const currentYearData = response.data?.current_year || response.data?.ctd || {};
        const previousYearData = response.data?.previous_year || response.data?.ytd || {};

        const cleanNumber = (value) => Number(String(value ?? '0').replace(/,/g, '')) || 0;

        const merged = monthOrder.map((month) => {
          const cur = currentYearData[month] || {};
          const prev = previousYearData[month] || {};

          const qty1 = cleanNumber(cur.meters);
          const amt1 = cleanNumber(cur.amount);
          const qty2 = cleanNumber(prev.meters);
          const amt2 = cleanNumber(prev.amount);

          return {
            month,
            qty1,
            amt1,
            qty2,
            amt2,
            qty1Display: formatIndianShort(qty1),
            amt1Display: formatIndianShort(amt1),
            qty2Display: formatIndianShort(qty2),
            amt2Display: formatIndianShort(amt2)
          };
        });

        setSales(merged);
      }
    } catch (error) {
      console.error('Error fetching Sales Comparison Report:', error);
    }
  };

  // Category Comparison Report
  const getCategoryCompReport = async () => {
    try {
      const response = await fetchBeatCustomerCategoryCompReport(user.userCode, customer.party_code);
      if (response.success) {
        const ctdArray = response.data.ctd.map((row) => ({
          category: row.category || 'Others',
          qty1: parseFloat(row.meters.replace(/,/g, '')), // remove commas
          amt1: parseFloat(row.amount),
          qty1Display: formatIndianShort(row.meters),
          amt1Display: formatIndianShort(row.amount)
        }));

        const ytdArray =
          response.data.ytd?.map((row) => ({
            category: row.category || 'Others',
            qty2: parseFloat(row.meters.replace(/,/g, '')),
            amt2: parseFloat(row.amount.replace(/,/g, '')),
            qty2Display: formatIndianShort(row.meters),
            amt2Display: formatIndianShort(row.amount)
          })) || [];

        // Merge ctd and ytd by index (or category)
        const merged = ctdArray.map((row, index) => ({
          category: row.category,
          qty1: row.qty1,
          amt1: row.amt1,
          qty2: ytdArray[index]?.qty2 || 0,
          amt2: ytdArray[index]?.amt2 || 0,
          qty1Display: row.qty1Display,
          amt1Display: row.amt1Display,
          qty2Display: ytdArray[index]?.qty2Display || formatIndianShort(0),
          amt2Display: ytdArray[index]?.amt2Display || formatIndianShort(0)
        }));
        setCategoryComp(merged);
      }
    } catch (error) {
      console.error('Error fetching Category Comparison Report:', error);
    }
  };

  const getTargetSale = async () => {
    try {
      const response = await fetchTargetSaleReport(user.userCode, customer.party_code);
      if (response.success) {
        const ctdArray = response.data.ctd.map((row) => ({
          partyName: row.party_name || 'Others',
          target1: row.target, // remove commas
          sale1: row.sale,
          target1Display: row.target,
          sale1Display: formatIndianShort(row.sale)
        }));

        const ytdArray =
          response.data.ytd?.map((row) => ({
            partyName: row.party_name || 'Others',
            target2: row.target, // remove commas
          sale2: row.sale,
          target2Display: row.target,
          sale2Display: formatIndianShort(row.sale)
          })) || [];

        // Merge ctd and ytd by index (or category)
        const merged = ctdArray.map((row, index) => ({
          partyName: row.partyName,
          target1: row.target1,
          sale1: row.sale1,
          target1: ytdArray[index]?.target1 || 0,
          sale1: ytdArray[index]?.sale1 || 0,
          target1Display: row.target1Display,
          sale1Display: row.sale1Display,
          target2Display: ytdArray[index]?.target2Display || formatIndianShort(0),
          sale2Display: ytdArray[index]?.sale2Display || formatIndianShort(0)
        }));
        setTargetSales(merged);
      }
    } catch (error) {
      console.error('Error fetching Target Sales Report:', error);
    }
  };

  return {
    cloading,
    customer,
    currentFY,
    previousFY,
    outstanding,
    sales,
    categoryComp,
    targetsales
  };
};

export default BeatsCustomerReportController;
